import React from 'react';
import { useNavigate } from 'react-router-dom';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Loader, ReloadIconButton, Title } from '../../elements';
import { AnimalType, WeightType } from '../../models/animal';
import {
  Avatar,
  Chip,
  Collapse,
  Grid,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { LoaderSize } from '../../constants/constants';
import SowIco from '../../static/sow01.jpg';
import PigletIco from '../../static/piglet01.jpg';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel,
  HealthSystemDomainAnimalsResultAnimalFosterMotherHistoryItemViewModel
} from '../../api/HealthSystemApi';
import { renderWeightCellDoubleWeightWithDate } from '../../utils/animalutils';
import NoDataMessage from '../shared/NoDataMessage';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';

interface AnimalViewFosterMotherTransfersProps {
  animalId: string;
  animalType: AnimalType;
}

interface FosterMotherHistoryEntryRow {
  historyEntry: HealthSystemDomainAnimalsResultAnimalFosterMotherHistoryItemViewModel;
  ageAtTransferDays: number | null;
}

interface PigletFosterMotherRow {
  piglet: HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel;
  history: FosterMotherHistoryEntryRow[];
  current: FosterMotherHistoryEntryRow | undefined;
}

const MS_PER_DAY = 86400000;

const AnimalViewFosterMotherTransfers = ({ animalId, animalType }: AnimalViewFosterMotherTransfersProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const apiService = new ApiService();

  const [rows, setRows] = React.useState<PigletFosterMotherRow[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [expandedPiglets, setExpandedPiglets] = React.useState<Set<string>>(new Set());

  const viewAnimal = (id?: string | null) => {
    if (id) {
      navigate(`/animal/${id}`);
    }
  };

  const toggleExpanded = (pigletId: string) => {
    const newExpanded = new Set(expandedPiglets);
    if (newExpanded.has(pigletId)) {
      newExpanded.delete(pigletId);
    } else {
      newExpanded.add(pigletId);
    }
    setExpandedPiglets(newExpanded);
  };

  const loadTransfers = async () => {
    apiService.api.animals
      .animalsGetAnimalPigletsBirthCycles({ AnimalId: animalId, AnimalType: animalType })
      .then((result) => {
        const pigletRows: PigletFosterMotherRow[] = result.data.data
          .flatMap((cycle) =>
            cycle.animals
              .filter((piglet) => piglet.fosterMotherHistory.length > 0)
              .map((piglet) => {
                const dateOfBirth = cycle.dateOfBirth ? new Date(cycle.dateOfBirth) : null;

                const history: FosterMotherHistoryEntryRow[] = [...piglet.fosterMotherHistory]
                  .sort((a, b) => new Date(b.transferredAt).getTime() - new Date(a.transferredAt).getTime())
                  .map((historyEntry) => {
                    const transferredAt = new Date(historyEntry.transferredAt);
                    const ageAtTransferDays = dateOfBirth
                      ? Math.max(0, Math.round((transferredAt.getTime() - dateOfBirth.getTime()) / MS_PER_DAY))
                      : null;

                    return { historyEntry, ageAtTransferDays };
                  });

                const current =
                  history.find((entry) => entry.historyEntry.fosterMotherId === piglet.fosterMotherId) ?? history[0];

                return { piglet, history, current };
              })
          )
          .sort((a, b) => {
            const aTime = a.current ? new Date(a.current.historyEntry.transferredAt).getTime() : 0;
            const bTime = b.current ? new Date(b.current.historyEntry.transferredAt).getTime() : 0;
            return bTime - aTime;
          });

        setRows(pigletRows);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => {
        setIsBusy(true);
        await loadTransfers();
      };

      loadData().finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
    }
  }, [loaded]);

  const renderAnimalChip = (
    id: string | null | undefined,
    number: string | null | undefined,
    avatarSrc: string,
    isSelf: boolean
  ) => {
    if (!id || !number) {
      return '-';
    }

    return (
      <Chip
        className={classes.formControl}
        avatar={<Avatar src={avatarSrc} />}
        label={number}
        color={isSelf ? 'default' : 'primary'}
        size="small"
        variant="outlined"
        onClick={isSelf ? undefined : () => viewAnimal(id)}
      />
    );
  };

  return (
    <Paper className={classes.paper} elevation={1}>
      <Grid container spacing={1}>
        <Grid container direction="row">
          <Grid container item spacing={1} xs={10}>
            <Grid item>
              <Avatar alt="Foster mother transfers" src={PigletIco} />
            </Grid>
            <Grid item>
              <Title>{t('f-foster-transfers-title')}</Title>
            </Grid>
            <Grid item>
              <Chip label={rows.length} variant="outlined" />
            </Grid>
          </Grid>

          <Grid item xs={2} className={classes.loadBtnGrid}>
            <ReloadIconButton action={() => setLoaded(false)} />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {rows.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  <TableCell />
                  <TableCell>{t('f-foster-transfers-piglet')}</TableCell>
                  <TableCell>{t('f-foster-transfers-weight')}</TableCell>
                  <TableCell>{t('f-foster-transfers-age')}</TableCell>
                  <TableCell>{t('f-foster-transfers-biological-mother')}</TableCell>
                  <TableCell>{t('f-foster-transfers-foster-mother')}</TableCell>
                  <TableCell>{t('f-com-date')}</TableCell>
                  <TableCell>{t('f-com-time')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const isExpanded = expandedPiglets.has(row.piglet.id);

                  return (
                    <React.Fragment key={row.piglet.id}>
                      <TableRow hover>
                        <TableCell>
                          {row.history.length > 1 && (
                            <IconButton onClick={() => toggleExpanded(row.piglet.id)} size="small">
                              <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            className={classes.formControl}
                            avatar={<Avatar src={PigletIco} />}
                            label={row.piglet.number}
                            size="small"
                            variant="outlined"
                            onClick={() => viewAnimal(row.piglet.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {renderWeightCellDoubleWeightWithDate(
                            row.current?.historyEntry.weightAtTransfer ?? null,
                            WeightType.Kilograms
                          )}
                        </TableCell>
                        <TableCell>
                          {row.current?.ageAtTransferDays != null
                            ? t('f-foster-transfers-age-days', { days: row.current.ageAtTransferDays })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {renderAnimalChip(
                            row.piglet.motherId,
                            row.piglet.motherNumber,
                            SowIco,
                            row.piglet.motherId === animalId
                          )}
                        </TableCell>
                        <TableCell>
                          {renderAnimalChip(
                            row.current?.historyEntry.fosterMotherId,
                            row.current?.historyEntry.fosterMotherNumber,
                            SowIco,
                            row.current?.historyEntry.fosterMotherId === animalId
                          )}
                          <Chip
                            className={classes.formControl}
                            label={t('f-com-current')}
                            color="primary"
                            size="small"
                          />
                          {row.history.length > 1 && (
                            <Chip
                              className={classes.formControl}
                              label={t('f-foster-transfers-changes-count', { count: row.history.length })}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>{row.current ? formatDate(row.current.historyEntry.transferredAt) : '-'}</TableCell>
                        <TableCell>{row.current ? formatTime(row.current.historyEntry.transferredAt) : '-'}</TableCell>
                      </TableRow>

                      {row.history.length > 1 && (
                        <TableRow>
                          <TableCell className={classes.collapseCell} colSpan={8}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Grid container className={classes.detailsContainer}>
                                <Grid item xs={12}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>{t('f-foster-transfers-foster-mother')}</TableCell>
                                        <TableCell>{t('f-foster-transfers-weight')}</TableCell>
                                        <TableCell>{t('f-foster-transfers-age')}</TableCell>
                                        <TableCell>{t('f-com-date')}</TableCell>
                                        <TableCell>{t('f-com-time')}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.history.map((entry) => (
                                        <TableRow key={entry.historyEntry.id} hover>
                                          <TableCell>
                                            {renderAnimalChip(
                                              entry.historyEntry.fosterMotherId,
                                              entry.historyEntry.fosterMotherNumber,
                                              SowIco,
                                              entry.historyEntry.fosterMotherId === animalId
                                            )}
                                            {entry === row.current && (
                                              <Chip
                                                className={classes.formControl}
                                                label={t('f-com-current')}
                                                color="primary"
                                                size="small"
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {renderWeightCellDoubleWeightWithDate(
                                              entry.historyEntry.weightAtTransfer ?? null,
                                              WeightType.Kilograms
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {entry.ageAtTransferDays !== null
                                              ? t('f-foster-transfers-age-days', { days: entry.ageAtTransferDays })
                                              : '-'}
                                          </TableCell>
                                          <TableCell>{formatDate(entry.historyEntry.transferredAt)}</TableCell>
                                          <TableCell>{formatTime(entry.historyEntry.transferredAt)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Grid>
                              </Grid>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            loaded && !isBusy && <NoDataMessage text={t('f-no-data-to-display')} />
          )}
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AnimalViewFosterMotherTransfers;
